type OrganizationRecord = { id: string };

type UnwrappableMutation<T> = {
  unwrap: () => Promise<T>;
};

export type OrganizationSaveResult =
  | { status: "success"; organizationId: string }
  | { status: "org-saved-photo-failed"; organizationId: string; error: unknown }
  | { status: "failed"; error: unknown };

export type SaveOrganizationParams = {
  editMode: boolean;
  existingOrganizationId?: string;
  /** After create succeeds but photo upload fails, retries must update this org — not POST again */
  pendingCreateOrganizationId?: string | null;
  apiBody: Record<string, unknown>;
  profilePictureFile?: File | Blob;
  addOrganization: (
    body: Record<string, unknown>
  ) => UnwrappableMutation<OrganizationRecord>;
  updateOrganization: (args: {
    orgId: string;
    orgData: Record<string, unknown>;
  }) => UnwrappableMutation<OrganizationRecord>;
  uploadProfilePicture: (args: {
    bodyFormData: FormData;
    orgId: string;
  }) => UnwrappableMutation<unknown>;
};

/**
 * Persists organization data then optionally uploads a profile photo.
 * Photo failures are isolated so a saved org can be retried without creating duplicates.
 */
export async function saveOrganizationWithProfilePicture(
  params: SaveOrganizationParams
): Promise<OrganizationSaveResult> {
  const {
    editMode,
    existingOrganizationId,
    pendingCreateOrganizationId,
    apiBody,
    profilePictureFile,
    addOrganization,
    updateOrganization,
    uploadProfilePicture,
  } = params;

  try {
    let organization: OrganizationRecord;

    if (editMode && existingOrganizationId) {
      organization = await updateOrganization({
        orgId: existingOrganizationId,
        orgData: apiBody,
      }).unwrap();
    } else if (pendingCreateOrganizationId) {
      organization = await updateOrganization({
        orgId: pendingCreateOrganizationId,
        orgData: apiBody,
      }).unwrap();
    } else {
      organization = await addOrganization(apiBody).unwrap();
    }

    if (profilePictureFile) {
      const formData = new FormData();
      formData.append("photo", profilePictureFile);

      try {
        await uploadProfilePicture({
          bodyFormData: formData,
          orgId: organization.id,
        }).unwrap();
      } catch (photoError) {
        return {
          status: "org-saved-photo-failed",
          organizationId: organization.id,
          error: photoError,
        };
      }
    }

    return { status: "success", organizationId: organization.id };
  } catch (error) {
    return { status: "failed", error };
  }
}
